import { ArtefactsFetchErrorMessage } from '@modules/bookmark/components/ArtefactsFetchErrorMessage';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { ScrollToTopButton } from '@modules/common/components/ScrollToTopButton';
import { Category } from '@modules/common/types';
import { getCategoryTypeFromString } from '@modules/common/utils';
import FeedbackDrawer from '@modules/feedback/components/FeedbackCard/FeedbackDrawer';
import FeedbackDrawerNavigationArrows from '@modules/feedback/components/FeedbackCard/FeedbackDrawerNavigationArrows';
import { FeedbackDetails } from '@modules/feedback/components/FeedbackDetails';
import {
  FeedbackListViewType,
  PREFETCH_TRIGGER_OFFSET,
  RECORDS_PER_PAGE,
} from '@modules/feedback/constants';
import {
  useFeedbackListInfiniteScroll,
  useHandleDetailViewPagination,
} from '@modules/feedback/hooks';
import { useGetFeedbackList } from '@modules/feedback/hooks/useGetFeedbackList';
import { useGetFeedbacksFromResponse } from '@modules/feedback/hooks/useGetFeedbacksFromResponse';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import {
  clearFeedbackRecordsCache,
  trackFeedbackEvents,
} from '@modules/feedback/utils';
import { useUpdateSavedFeedbackListStoreIfRequired } from '@modules/savedFeedbackList/hooks';
import { Box, Stack } from '@mui/material';
import { GetRecordsResponseViewFragment, SortBy } from 'generated/graphql';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useMemo, useState } from 'react';
import { useDeepCompareEffect, useUnmount } from 'react-use';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import getMatchedFeedbackRecords from 'v2/core/services/records/getMatchedFeedbackRecords';
import { FeedbackMetaData } from 'v2/store/useViewFeedbackStore';
import { QuickFilters } from 'v2/views/components/charts/tooltip/common';
import { FeedbackOptions } from 'v2/views/components/feedbackListViewer/types';
import FeedbackCard from '../FeedbackCard/FeedbackCard';
import Empty from './EmptyFeedbackList';

export interface FeedbackListProps
  extends Omit<ReturnType<typeof useGetFeedbackList>, 'fetchRecords'> {
  options?: FeedbackOptions;
  selectedFeedbackMetadata?: FeedbackMetaData[];
  isLoadingMore: boolean;
  variant?: 'compact' | 'default';
  sortBy?: SortBy;
  queryString: string;
  allowedPopoverOptions?: QuickFilters[];
  queryContainer?: QueryContainer;
  isSummaryView?: boolean;
  fetchRecords: Function;
  feedbackListViewType?: FeedbackListViewType;
  shouldShowRemoveFromCollectionButton?: boolean;
  onlyShowFeedbackIDs?: string[];
  shouldShowScrollToTopButton?: boolean;
}

const FeedbackList = ({
  options,
  selectedFeedbackMetadata,
  isLoadingMore,
  variant = 'default',
  sortBy,
  hits = 0,
  error,
  fetchMore,
  status,
  feedbacks: fetchedFeedbacksForPage = [],
  fetchRecords,
  called,
  client,
  queryContainer,
  isSummaryView = true,
  feedbackListViewType = FeedbackListViewType.DEFAULT,
  shouldShowRemoveFromCollectionButton = false,
  onlyShowFeedbackIDs,
  shouldShowScrollToTopButton = true,
}: FeedbackListProps) => {
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const [isEditPredictionsMode, setIsEditPredictionsMode] = useState(
    !!recordInEditMode,
  );

  const [isFeedbackDetailsDrawerVisible, setIsFeedbackDetailsDrawerVisible] =
    useState(false);

  const [feedbacksList, setFeedbacksList] = useState(fetchedFeedbacksForPage);

  const [currentFeedbackInDetailView, setCurrentFeedbackInDetailView] =
    useState<Feedback | undefined>(undefined);

  const [
    currentFeedbackSelectedLabelsInDetailView,
    setCurrentFeedbackSelectedLabelsInDetailView,
  ] = useState<FeedbackLabel[]>([]);

  const [currentFeedbackIndex, setCurrentFeedbackIndexInDetailView] =
    useState(-1);

  const [feedbackRecords, setFeedbackRecords] =
    useState<GetRecordsResponseViewFragment>();

  const { feedbackRecords: updatedFeedbackRecords } =
    useGetFeedbacksFromResponse(feedbackRecords);

  useEffect(() => {
    clearFeedbackRecordsCache(client);
    setFeedbacksList([]);
    fetchRecords();
    // eslint-disable-next-line
  }, [queryContainer, sortBy, fetchRecords]);

  useUnmount(() => {
    // clear records cache on mount to save up space
    // and to fix stale cursor issue

    clearFeedbackRecordsCache(client);
  });

  useEffect(() => {
    setIsEditPredictionsMode(!!recordInEditMode);
  }, [recordInEditMode]);

  useDeepCompareEffect(() => {
    if (onlyShowFeedbackIDs) {
      return setFeedbacksList((existing) =>
        [...existing, ...fetchedFeedbacksForPage].filter((feedback) =>
          onlyShowFeedbackIDs.includes(feedback.id),
        ),
      );
    }
    setFeedbacksList((existing) => [...existing, ...fetchedFeedbacksForPage]);
  }, [fetchedFeedbacksForPage, onlyShowFeedbackIDs]);

  // We use `matchedFeedbacks` for showing the feedbacks in the list.
  // We are doing a bunch of manipulations on the feedbacks to get the matched
  // feedbacks along with correct page number.

  const matchedFeedbacks =
    feedbackListViewType === FeedbackListViewType.DEFAULT
      ? uniqBy(getMatchedFeedbackRecords(feedbacksList, options), 'id')
      : uniqBy(feedbacksList, 'id');

  useUpdateSavedFeedbackListStoreIfRequired(
    feedbackListViewType === FeedbackListViewType.SAVED ? feedbacksList : [],
  );

  const isLoading = IsLoading(status) || isLoadingMore;

  const setTriggerPointInViewRef = useFeedbackListInfiniteScroll({
    feedbacks: matchedFeedbacks,
    fetchMore,
    totalRecords: hits,
    setFeedbacks: setFeedbacksList,
    isDisabled: isLoading,
    onlyShowFeedbackIDs,
  });

  const selectedCategories = selectedFeedbackMetadata
    ?.map((feedbackMetadata) => getCategoryTypeFromString(feedbackMetadata.id))
    .filter((categoryType): categoryType is Category => Boolean(categoryType));
  const selectedLabelsMetadata = selectedFeedbackMetadata?.filter(
    (feedbackMetadata) =>
      getCategoryTypeFromString(feedbackMetadata.id) === null,
  );

  const feedbackMetadata = useMemo(
    () =>
      options?.match?.type !== 'SEARCH' && !selectedLabelsMetadata?.[0]?.id
        ? // We need to pass only the selected feedback metadata
          // when the query is not a search query.
          ([
            {
              id: options?.match?.id,
            },
            ...(options?.match?.type === 'REASON' &&
            options?.match.parentEntityID
              ? [
                  {
                    id: options?.match?.parentEntityID,
                  },
                ]
              : []),
          ] as FeedbackMetaData[])
        : selectedLabelsMetadata,
    [options, selectedLabelsMetadata],
  );

  const handleOnCloseDrawer = () => {
    if (isEditPredictionsMode) {
      dispatch({
        type: FeedbackOnFeedbackActionType.RESET_RECORD_IN_EDIT_MODE,
        payload: {},
      });
    }
    setIsFeedbackDetailsDrawerVisible(false);
    setIsEditPredictionsMode(false);
    trackFeedbackEvents({
      eventName: 'Feedback Card Collapsed',
      feedbackID: currentFeedbackInDetailView?.id ?? '',
    });

    setCurrentFeedbackInDetailView(undefined);
    setCurrentFeedbackIndexInDetailView(-1);
  };

  const onPageChange = useHandleDetailViewPagination({
    feedbacks: matchedFeedbacks,
    currentFeedbackIndex,
    isFeedbackDetailsDrawerVisible,
    feedbackListViewType,
    selectedCategories,
    feedbackMetadata,
    setCurrentFeedbackInDetailView,
    setCurrentFeedbackIndexInDetailView,
    setCurrentFeedbackSelectedLabelsInDetailView,
  });

  useDeepCompareEffect(() => {
    if (!updatedFeedbackRecords || !matchedFeedbacks) {
      return;
    }

    if (onlyShowFeedbackIDs) {
      return setFeedbacksList((existing) =>
        [...existing, ...fetchedFeedbacksForPage].filter((feedback) =>
          onlyShowFeedbackIDs.includes(feedback.id),
        ),
      );
    }

    setFeedbacksList([...matchedFeedbacks, ...updatedFeedbackRecords]);
  }, [updatedFeedbackRecords, onlyShowFeedbackIDs]);

  const onLabelClick = (
    labels: FeedbackLabel[],
    feedback: Feedback,
    index: number,
  ) => {
    setIsFeedbackDetailsDrawerVisible(true);
    setCurrentFeedbackInDetailView(feedback);
    setCurrentFeedbackIndexInDetailView(index);
    setCurrentFeedbackSelectedLabelsInDetailView(labels);
  };

  if (error && !IsLoading(status)) {
    return (
      <ArtefactsFetchErrorMessage
        onRetry={() => {
          clearFeedbackRecordsCache(client);
          setFeedbacksList([]);
          fetchRecords();
        }}
      />
    );
  }

  if (
    (!feedbacksList.length || !feedbacksList || !matchedFeedbacks) &&
    !IsLoading(status) &&
    !hits
  ) {
    return <Empty />;
  }

  const infiniteScrollTriggerIndex =
    fetchedFeedbacksForPage.length === RECORDS_PER_PAGE
      ? matchedFeedbacks.length - PREFETCH_TRIGGER_OFFSET // 4th last record, so that we can prefetch the next page
      : matchedFeedbacks.length - 1; // last record, doesn't matter because there is no more data to fetch

  return (
    <>
      <Stack height="100%" position="relative">
        {matchedFeedbacks?.map((feedback, index) => {
          return (
            <Box
              key={`${feedback.id}-${index}`}
              ref={
                index === infiniteScrollTriggerIndex
                  ? setTriggerPointInViewRef
                  : undefined
              }
            >
              <FeedbackCard
                feedback={feedback}
                selectedFeedbackMetadata={feedbackMetadata}
                selectedCategories={selectedCategories}
                feedbackIndex={index}
                feedbacks={matchedFeedbacks}
                isLoadingMore={IsLoading(status)}
                variant={variant}
                isSummaryView={isSummaryView}
                feedbackListViewType={feedbackListViewType}
                onViewDetailsButtonClick={(labels: FeedbackLabel[]) =>
                  onLabelClick(labels, feedback, index)
                }
                isFeedbackDetailsDrawerVisible={isFeedbackDetailsDrawerVisible}
                setIsFeedbackDetailsDrawerVisible={
                  setIsFeedbackDetailsDrawerVisible
                }
                shouldShowRemoveFromCollectionButton={
                  shouldShowRemoveFromCollectionButton
                }
              />
            </Box>
          );
        })}
        {isLoading && (
          <Stack
            height="100%"
            width="100%"
            pb={shouldShowScrollToTopButton ? 7 : 0}
          >
            <CircularLoader
              sx={{
                my: 3,
              }}
            />
          </Stack>
        )}
        {shouldShowScrollToTopButton && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
            }}
          >
            <ScrollToTopButton
              containerSx={{
                position: 'absolute',
              }}
              onClick={() => {
                trackFeedbackEvents({
                  eventName: 'Feedback List - Scroll To Top Button Clicked',
                  feedbackID: undefined,
                });
              }}
            />
          </Box>
        )}
      </Stack>
      <FeedbackDrawer
        unmountOnClose={true}
        isOpen={
          isFeedbackDetailsDrawerVisible && Boolean(currentFeedbackInDetailView)
        }
        onClose={handleOnCloseDrawer}
      >
        {currentFeedbackInDetailView && (
          <FeedbackDetails
            feedback={currentFeedbackInDetailView}
            onDrawerClose={handleOnCloseDrawer}
            selectedLabels={currentFeedbackSelectedLabelsInDetailView}
            setCurrentFeedbackIndex={setCurrentFeedbackIndexInDetailView}
            setCurrentFeedback={setCurrentFeedbackInDetailView}
            feedbacks={matchedFeedbacks}
            currentFeedbackIndex={currentFeedbackIndex}
            totalRecords={hits}
            isLoadingMore={IsLoading(status)}
            fetchMore={fetchMore}
            setFeedbackRecords={setFeedbackRecords}
            footer={
              !isEditPredictionsMode && (
                <FeedbackDrawerNavigationArrows
                  onPageChange={onPageChange}
                  feedbacks={matchedFeedbacks}
                  currentFeedbackIndex={currentFeedbackIndex}
                  totalRecords={hits}
                />
              )
            }
          />
        )}
      </FeedbackDrawer>
    </>
  );
};

export default FeedbackList;
